import api from "./axios";

/**
 * Decode the payload of a JWT without verifying it. Verification happens on
 * the backend; here we only need the `exp` claim so the UI can tell when the
 * session will stop working.
 */
const decodeToken = (token) => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const AuthService = {
  login: async (credentials) => {
    try {
      const response = await api.post("/auth/login", credentials);

      // Store token in localStorage
      localStorage.setItem("token", response.data.accessToken);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      return response.data;
    } catch (error) {
      console.error("Error logging in:", error);
      throw error;
    }
  },

  register: async (credentials) => {
    try {
      const response = await api.post("/auth/register", credentials);

      // Store token in localStorage
      localStorage.setItem("token", response.data.accessToken);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      return response.data;
    } catch (error) {
      console.error("Error registering:", error);
      throw error;
    }
  },

  /**
   * Whether the backend still accepts self-service registration (only true
   * until the first admin account exists).
   */
  isRegistrationOpen: async () => {
    const response = await api.get("/auth/registration-status");
    return !!response.data.open;
  },

  /**
   * Ask the backend whether the stored token is still valid. Resolves with the
   * user on success; rejects (and the axios interceptor clears the session)
   * if the token is rejected.
   */
  verifySession: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },

  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  getToken: () => localStorage.getItem("token"),

  /**
   * Expiry time of the stored token in epoch milliseconds, or null if there
   * is no token or it carries no `exp` claim.
   */
  getTokenExpiry: () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = decodeToken(token);
    return payload?.exp ? payload.exp * 1000 : null;
  },

  isTokenExpired: () => {
    const expiry = AuthService.getTokenExpiry();
    return expiry !== null && Date.now() >= expiry;
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("token") && !AuthService.isTokenExpired();
  },
};

export default AuthService;
